import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";
import { Modal, Button } from "portal/components/ui";
import billingService from "portal/services/billingService";
import allRoutes from "client/constants/allRoutes";
import { queryClient } from "queryClient";

// The next working day, the front desk is shown every patient whose attendance was not
// marked on a past working day — the same per-patient, per-day attendance as the
// Attendance page, with the same choices. The month-end wallet settlement waits on those
// days until the 5th of the next month; on the 6th it settles the month anyway, and a
// session still not marked gets no credit (backend walletSettlementService). Once a day per
// user: closing it keeps it closed until tomorrow (the Dashboard's card opens it on demand), and
// it stops coming once everything is marked.

// The list is one React Query entry, shared with the Dashboard's LastMonthAttendanceBanner:
// the popup reads it through the same client, so a Dashboard load asks the server once for
// both, and a save here asks again so the banner's count drops.
export const UNMARKED_ATTENDANCE_KEY = ["unmarkedAttendance"];

// The Dashboard's "Attendance Not Marked" card opens the popup again — after "Remind me
// tomorrow" too. The popup is mounted once, in Layout, so the card asks it through a window event.
const OPEN_EVENT = "unmarkedAttendance:open";
export const openUnmarkedAttendance = () => window.dispatchEvent(new Event(OPEN_EVENT));

// The front desk: whoever marks the Attendance page and runs the schedule (Admin,
// Reception) — not a therapist or the accountant, who can mark attendance too.
export const canMarkAttendance = (user, permissions = []) =>
  user?.RoleId === 1 || user?.RoleId === 2
  || (permissions.includes("manage_attendance") && permissions.includes("manage_scheduling"));

// What useApiQuery caches under that key: the response's data, or an error.
const fetchUnmarkedAttendance = () => queryClient.fetchQuery({
  queryKey: UNMARKED_ATTENDANCE_KEY,
  queryFn: async () => {
    const res = await billingService.getUnmarkedAttendance();
    if (res?.rescode === 1) return res.data;
    throw new Error(res?.message || "Request failed");
  },
});

const dayMonth = (ymd) => {
  const d = moment(ymd, "YYYY-MM-DD", true);
  return d.isValid() ? d.format("D MMM") : ymd;
};

// What the marks decide, in the popup's subtitle. The 1st to the 5th: last month's deadline and
// the day it is settled anyway. After that: the rule, which the next month's deadline follows.
// A last month from before the rule (settles_on null) still waits for its marks.
export const settleNote = (lastMonth) => {
  if (lastMonth?.in_window) {
    return `mark last month's by ${dayMonth(lastMonth.mark_by)}. On ${dayMonth(lastMonth.settles_on)} it is settled anyway, and a session still not marked gets no credit.`;
  }
  if (lastMonth?.settles_on) {
    return "a month's wallet credits are settled on the 6th of the next month, and a session still not marked by then gets no credit.";
  }
  return "month-end wallet credits wait for them.";
};

const STATUS_CHOICES = [
  { value: "", label: "— Not marked —" },
  { value: "PRESENT", label: "Present" },
  { value: "PATIENT_OFF", label: "Patient Off" },
  { value: "PLANNED_LEAVE", label: "Planned Leave" },
  { value: "CENTRE_OFF", label: "Centre Off" },
];

const todayKey = () => moment().format("YYYY-MM-DD");
const dismissKey = (uid) => `unmarkedAttendancePopup:${uid}`;
const rowKey = (r) => `${r.patient_id}|${r.date}`;

function readDismissed(uid) {
  try { return localStorage.getItem(dismissKey(uid)); } catch (e) { return null; }
}
function writeDismissed(uid) {
  try { localStorage.setItem(dismissKey(uid), todayKey()); } catch (e) { /* private mode — shows again next load */ }
}

const UnmarkedAttendancePopup = () => {
  const user = useSelector((state) => state.auth.user);
  const permissions = useSelector((state) => state.auth.permissions) || [];
  const canMark = canMarkAttendance(user, permissions);
  // The logged-in user's id is `uid` on the auth slice (the JWT payload's name for it).
  const userId = user?.uid ?? user?.UserId;

  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [choices, setChoices] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);
  // The server's `last_month` (walletSettlementService.settlesOn / FORCE_DAY): whether last month is
  // settled anyway, and when — the popup's words follow it, never the browser's date.
  const [lastMonth, setLastMonth] = useState(null);

  useEffect(() => {
    if (!canMark || !userId) return;
    let cancelled = false;
    // `onDemand`: opened from the Dashboard card, which says so when there is nothing to show.
    const show = (onDemand) => fetchUnmarkedAttendance()
      .then((data) => {
        const list = data?.days || [];
        if (cancelled) return;
        if (list.length === 0) {
          if (onDemand) toast.info("Attendance is marked for every past working day");
          return;
        }
        setRows(list);
        setLastMonth(data?.last_month || null);
        setChoices({});
        setExpanded(list[0].date);
        setOpen(true);
      })
      .catch(() => { if (onDemand && !cancelled) toast.error("Could not load attendance"); });
    if (readDismissed(userId) !== todayKey()) show(false);
    const onOpen = () => show(true);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => { cancelled = true; window.removeEventListener(OPEN_EVENT, onOpen); };
  }, [canMark, userId]);

  const byDate = useMemo(() => {
    const groups = [];
    rows.forEach((r) => {
      const last = groups[groups.length - 1];
      if (last && last.date === r.date) last.rows.push(r);
      else groups.push({ date: r.date, rows: [r] });
    });
    return groups;
  }, [rows]);

  const chosen = rows.filter((r) => choices[rowKey(r)]);

  const close = () => {
    writeDismissed(userId);
    setOpen(false);
  };

  const markAllPresent = (group) => {
    setChoices((prev) => {
      const next = { ...prev };
      group.rows.forEach((r) => { if (!next[rowKey(r)]) next[rowKey(r)] = "PRESENT"; });
      return next;
    });
  };

  const save = async () => {
    if (chosen.length === 0) return;
    setSaving(true);
    try {
      const res = await billingService.bulkMarkAttendance({
        records: chosen.map((r) => ({ patient_id: r.patient_id, date: r.date, status: choices[rowKey(r)] })),
      });
      if (res?.rescode !== 1) {
        toast.error(res?.message || "Could not save attendance");
        return;
      }
      toast.success(`Attendance saved for ${chosen.length} patient${chosen.length === 1 ? "" : "s"}`);
      // The Dashboard banner counts the same list: stale now, asked again while it shows.
      queryClient.invalidateQueries({ queryKey: UNMARKED_ATTENDANCE_KEY });
      const done = new Set(chosen.map(rowKey));
      const left = rows.filter((r) => !done.has(rowKey(r)));
      setRows(left);
      setChoices((prev) => {
        const next = { ...prev };
        done.forEach((k) => delete next[k]);
        return next;
      });
      if (left.length === 0) setOpen(false);
      else if (!left.some((r) => r.date === expanded)) setExpanded(left[0].date);
    } catch (e) {
      toast.error(e?.message || "Could not save attendance");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      isOpen={open}
      onClose={close}
      size="lg"
      title="Attendance not marked"
      subtitle={`${rows.length} patient-day${rows.length === 1 ? "" : "s"} on past working days still need attendance — ${settleNote(lastMonth)}`}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2 w-full">
          <Button variant="ghost" onClick={close} disabled={saving}>Remind me tomorrow</Button>
          <Button variant="primary" onClick={save} loading={saving} disabled={chosen.length === 0}>
            {chosen.length ? `Save ${chosen.length}` : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
        {byDate.map((group) => {
          const isOpen = expanded === group.date;
          return (
            <div key={group.date} className="border border-surface-border rounded-xl">
              <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                <button
                  type="button"
                  onClick={() => setExpanded(isOpen ? null : group.date)}
                  className="text-left text-body font-semibold text-content-primary"
                >
                  {moment(group.date).format("ddd, D MMM YYYY")}
                  <span className="ml-2 text-caption font-normal text-content-tertiary">
                    {group.rows.length} patient{group.rows.length === 1 ? "" : "s"}
                  </span>
                </button>
                <div className="flex items-center gap-3 text-caption">
                  {isOpen && (
                    <button type="button" onClick={() => markAllPresent(group)} className="text-brand-600 font-medium hover:underline">
                      All Present
                    </button>
                  )}
                  <Link
                    to={`${allRoutes.attendanceManagement}?tab=patient&date=${group.date}`}
                    onClick={close}
                    className="text-content-secondary hover:underline"
                  >
                    Open Attendance
                  </Link>
                </div>
              </div>
              {isOpen && (
                <div className="border-t border-surface-border divide-y divide-surface-border">
                  {group.rows.map((r) => (
                    <div key={rowKey(r)} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2">
                      <div className="flex-1 min-w-[10rem]">
                        <p className="text-body text-content-primary m-0">{r.patient_name || `Patient #${r.patient_id}`}</p>
                        <p className="text-caption text-content-tertiary m-0">
                          {[r.first_time, r.services, `${r.sessions} session${r.sessions === 1 ? "" : "s"}`].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                      <select
                        aria-label={`Attendance for ${r.patient_name || "patient"} on ${r.date}`}
                        value={choices[rowKey(r)] || ""}
                        onChange={(e) => setChoices((prev) => ({ ...prev, [rowKey(r)]: e.target.value }))}
                        className="px-2 py-1.5 border border-surface-border rounded-lg text-caption text-content-primary bg-surface-card"
                      >
                        {STATUS_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default UnmarkedAttendancePopup;
